import { IRegion, IRoute } from'../../../dip/library/di-hnf/di-hnf.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';
import navigation from'../../../pip/library/pi-navigation/pi-navigation.js';

const getRegion = () => 'region';
export type TRegionChange = {
  update: IRegion[];
  unload: IRegion[];
  load: IRegion[];
  query: IRegion[];
};

export type TStateSubscriptionChange = {
  update: string[];
  enter: string[];
  exit: string[];
  query: string[];
};

/**
 * a set of functions to calculate transitions in the state machine
 */
export class StateChange {
  /**
   * calculate the difference between route to load and unload
   */
  deriveRegionChange(requested: IRoute, active: IRoute): TRegionChange {
    const requestRegionsDB = f1.to.record(getRegion, requested.regions);
    const activeRegionsDB = f1.to.record(getRegion, active.regions);

    const unloadDB = f1.filter(({ region, view, view_tag, view_preloaded }) => {
      return (
        requestRegionsDB[region] == null ||
        requestRegionsDB[region].view !== view ||
        requestRegionsDB[region].view_tag !== view_tag ||
        requestRegionsDB[region].view_preloaded !== view_preloaded ||
        requestRegionsDB[region].exclude === true
      );
    }, activeRegionsDB);

    const updateDB = f1.filter(({ region, exclude, view, view_tag, view_preloaded }) => {
      return (
        exclude !== true &&
        (requestRegionsDB[region] == null ||
          (requestRegionsDB[region].exclude !== true &&
            (requestRegionsDB[region].view === view ||
              requestRegionsDB[region].view_tag === view_tag ||
              requestRegionsDB[region].view_preloaded === view_preloaded)))
      );
    }, activeRegionsDB);

    const queryDB = updateDB;

    const loadDB = f1.filter(({ region, exclude, view, view_tag, view_preloaded }) => {
      return (
        exclude !== true &&
        (activeRegionsDB[region] == null ||
          activeRegionsDB[region].view !== view ||
          activeRegionsDB[region].view_tag !== view_tag ||
          activeRegionsDB[region].view_preloaded !== view_preloaded)
      );
    }, requestRegionsDB);

    return {
      update: f1.to.array(updateDB),
      unload: f1.to.array(unloadDB),
      query: f1.to.array(queryDB),
      load: f1.to.array(loadDB)
    };
  }

  /**
   * calculate the enter and exit subscription events between a state change
   */
  deriveStateSubscriptions(requested: IRoute, active: IRoute): TStateSubscriptionChange {
    const oldURI = navigation.location.active.hash;
    const newURI = window.location.hash;

    const oldQueryParams = f1.safe('', f1.nth(1, f1.split('?', oldURI)));
    const newQueryParams = f1.safe('', f1.nth(1, f1.split('?', newURI)));
    const hasQueryChange = oldQueryParams !== newQueryParams;

    const toState = f1.split('.', `${requested.abs_state || ''}`);
    const fromState = f1.split('.', `${active.abs_state || ''}`);

    const enterStates = [...f1.safe([], requested.parents), requested];
    const exitStates = [...f1.safe([], active.parents), active];
    const toUpdate = [...f1.safe([], active.parents), active];
    const toQuery = hasQueryChange ? [...f1.safe([], active.parents), active] : [];

    let toFlag = false;
    const toEnter = f1.filter((s, index) => {
      const hasMatch = fromState[index] !== s.state;
      if (hasMatch) {
        toFlag = true;
      }

      return toFlag;
    }, enterStates);

    let fromFlag = false;
    const toExit = f1.filter((s, index) => {
      const hasMatch = toState[index] !== s.state;
      if (hasMatch) {
        fromFlag = true;
      }

      return fromFlag;
    }, exitStates);

    return {
      update: f1.compact(f1.map((v) => v.on_update, toUpdate)),
      enter: f1.compact(f1.map((v) => v.on_enter, toEnter)),
      exit: f1.compact(f1.map((v) => v.on_exit, toExit)),
      query: f1.compact(f1.map((v) => v.on_query, toQuery))
    };
  }

  /**
   * calculate enter/exit events from regions
   */
  deriveRegionSubscriptions(regionChange: TRegionChange): TStateSubscriptionChange {
    const { update, unload, load, query } = regionChange;

    return {
      update: f1.compact(f1.map((r) => r.on_update, update)),
      enter: f1.compact(f1.map((r) => r.on_enter, load)),
      exit: f1.compact(f1.map((r) => r.on_exit, unload)),
      query: f1.compact(f1.map((r) => r.on_query, query))
    };
  }
}
